import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Goal } from "@shared/schema";
import {
  Flame, Beef, Wheat, Droplets, AlertCircle, Ban, Candy, Leaf, Beaker,
  Plus, Target, TrendingUp, TrendingDown, Minus, Pencil, Trash2, ExternalLink, Salad, Share2
} from "lucide-react";
import { Link } from "wouter";
import ShareDialog from "@/components/share-dialog";

// ─── Metric definitions ────────────────────────────────────────────────────

const FOOD_METRICS = [
  { key: "calories",     label: "Calories",      unit: "kcal", color: "#f97316", Icon: Flame },
  { key: "protein",      label: "Protein",        unit: "g",    color: "#3b82f6", Icon: Beef },
  { key: "carbs",        label: "Carbohydrates",  unit: "g",    color: "#a855f7", Icon: Wheat },
  { key: "fat",          label: "Total Fat",      unit: "g",    color: "#eab308", Icon: Droplets },
  { key: "saturatedFat", label: "Saturated Fat",  unit: "g",    color: "#ef4444", Icon: AlertCircle },
  { key: "transFat",     label: "Trans Fat",      unit: "g",    color: "#dc2626", Icon: Ban },
  { key: "sugar",        label: "Sugar",          unit: "g",    color: "#ec4899", Icon: Candy },
  { key: "fiber",        label: "Fiber",          unit: "g",    color: "#22c55e", Icon: Leaf },
  { key: "sodium",       label: "Sodium",         unit: "mg",   color: "#64748b", Icon: Beaker },
] as const;

type FoodMetricKey = (typeof FOOD_METRICS)[number]["key"];

// ─── Sparkline ─────────────────────────────────────────────────────────────

function SparkLine({ data, target, color }: { data: number[]; target: number; color: string }) {
  if (data.length < 2) return null;
  const W = 220, H = 56, P = 4;
  const maxV = Math.max(target * 1.4, ...data, 1);
  const y = (v: number) => P + (H - P * 2) * (1 - v / maxV);
  const xStep = (W - P * 2) / Math.max(data.length - 1, 1);
  const pts = data.map((v, i) => `${P + i * xStep},${y(v)}`).join(" ");
  const ty = y(target);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" aria-hidden>
      <line x1={P} y1={ty} x2={W - P} y2={ty} stroke={color} strokeDasharray="4,2" strokeOpacity={0.35} strokeWidth={1.5} />
      <polyline fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" points={pts} />
      {data.map((v, i) => (
        <circle key={i} cx={P + i * xStep} cy={y(v)} r={3} fill={color} stroke="white" strokeWidth={1.5} />
      ))}
    </svg>
  );
}

// ─── Trend badge ───────────────────────────────────────────────────────────

function TrendBadge({ data }: { data: number[] }) {
  if (data.length < 2) return null;
  const recent = data.slice(-3);
  const avg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const prev = data.slice(-6, -3);
  if (prev.length === 0) return null;
  const prevAvg = prev.reduce((a, b) => a + b, 0) / prev.length;
  const pct = prevAvg === 0 ? 0 : ((avg - prevAvg) / prevAvg) * 100;
  if (Math.abs(pct) < 2) return <span className="text-xs text-muted-foreground flex items-center gap-1"><Minus className="w-3 h-3" />Stable</span>;
  return pct > 0
    ? <span className="text-xs text-orange-600 flex items-center gap-1"><TrendingUp className="w-3 h-3" />+{pct.toFixed(0)}%</span>
    : <span className="text-xs text-green-600 flex items-center gap-1"><TrendingDown className="w-3 h-3" />{pct.toFixed(0)}%</span>;
}

// ─── Day labels ────────────────────────────────────────────────────────────

function DayLabels({ days }: { days: number }) {
  const labels: string[] = [];
  const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    labels.push(i === 0 ? "Today" : DAY[d.getDay()]);
  }
  return (
    <div className="flex justify-between px-1 mt-0.5">
      {labels.map((l, i) => <span key={i} className="text-[9px] text-muted-foreground">{l}</span>)}
    </div>
  );
}

// ─── Goal Card ─────────────────────────────────────────────────────────────

interface GoalCardProps {
  goal: Goal;
  todayValue: number;
  trendData: number[];
  onEdit: (g: Goal) => void;
  onDelete: (id: string) => void;
  onShare: (g: Goal) => void;
}

function GoalCard({ goal, todayValue, trendData, onEdit, onDelete, onShare }: GoalCardProps) {
  const metric = FOOD_METRICS.find(m => m.key === goal.healthMetric);
  if (!metric) return null;

  const { Icon, color, unit, label } = metric;
  const target = goal.customGoalTarget ?? 0;
  const pct = target > 0 ? Math.min(100, (todayValue / target) * 100) : 0;
  const isOver = todayValue > target && target > 0;
  const isOnTrack = pct >= 80 && pct <= 110;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onEdit(goal)}
    >
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg" style={{ backgroundColor: color + "18" }}>
              <Icon className="w-4 h-4" style={{ color }} />
            </div>
            <div>
              <div className="font-semibold text-sm leading-tight">{goal.title}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          </div>
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onShare(goal)}>
              <Share2 className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(goal)}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDelete(goal.id)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-2xl font-bold" style={{ color }}>
              {todayValue % 1 === 0 ? todayValue : todayValue.toFixed(1)}
            </span>
            <span className="text-sm text-muted-foreground ml-1">{unit}</span>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium">Goal: {target}{unit}</div>
            <div className="flex items-center justify-end gap-1 mt-0.5">
              {isOver
                ? <Badge variant="destructive" className="text-[10px] h-4 px-1">Over</Badge>
                : isOnTrack
                  ? <Badge className="text-[10px] h-4 px-1 bg-green-500">On Track</Badge>
                  : <Badge variant="outline" className="text-[10px] h-4 px-1">{pct.toFixed(0)}%</Badge>
              }
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <Progress value={pct} className="h-2" style={{ "--progress-color": isOver ? "#ef4444" : color } as any} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">7-day trend</span>
            <TrendBadge data={trendData} />
          </div>
          <SparkLine data={trendData} target={target} color={color} />
          <DayLabels days={trendData.length} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Create / Edit dialog ──────────────────────────────────────────────────

interface DialogState {
  open: boolean;
  goal: Goal | null;
}

function GoalDialog({
  state,
  onClose,
  usedMetrics,
}: {
  state: DialogState;
  onClose: () => void;
  usedMetrics: Set<string>;
}) {
  const { toast } = useToast();
  const isEdit = !!state.goal;

  const [metric, setMetric] = useState<FoodMetricKey>(
    (state.goal?.healthMetric as FoodMetricKey) ?? "calories"
  );
  const [target, setTarget] = useState<string>(
    state.goal?.customGoalTarget?.toString() ?? ""
  );
  const [title, setTitle] = useState<string>(state.goal?.title ?? "");

  const metricInfo = FOOD_METRICS.find(m => m.key === metric)!;

  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/goals", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      toast({ title: "Food goal created" });
      onClose();
    },
    onError: () => {
      toast({ title: "Couldn't save the food goal", description: "Please try again.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => apiRequest("PATCH", `/api/goals/${state.goal!.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      toast({ title: "Food goal updated" });
      onClose();
    },
    onError: () => {
      toast({ title: "Couldn't save the food goal", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleSave = () => {
    const t = parseFloat(target);
    if (!metric || isNaN(t) || t <= 0) {
      toast({ title: "Enter a valid target", variant: "destructive" });
      return;
    }
    const autoTitle = title.trim() || `Daily ${metricInfo.label} Goal`;
    const payload = {
      title: autoTitle,
      // The goals API requires a category and a (nullable) deadline; food
      // goals are open-ended health goals, so use the Health category and no
      // deadline. Omitting these made the server reject the save silently.
      category: "Health",
      deadline: null,
      goalSource: "food",
      healthMetric: metric,
      customGoalTarget: t,
      customGoalType: "number",
      customGoalTitle: `${metricInfo.label} (${metricInfo.unit})`,
      progressCalculationType: "custom",
      status: "active",
    };
    if (isEdit) updateMutation.mutate(payload);
    else createMutation.mutate(payload);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={state.open} onOpenChange={o => !o && onClose()}>
      <DialogContent >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Food Goal" : "Create Food Goal"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Metric to track</Label>
            <Select value={metric} onValueChange={v => {
              setMetric(v as FoodMetricKey);
              if (!title) setTitle("");
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FOOD_METRICS.map(m => {
                  const alreadyUsed = !isEdit && usedMetrics.has(m.key);
                  return (
                    <SelectItem key={m.key} value={m.key} disabled={alreadyUsed}>
                      {m.label} ({m.unit}){alreadyUsed ? " — already tracked" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Daily target ({metricInfo.unit})</Label>
            <Input
              type="number"
              min={0}
              step={metric === "calories" ? 50 : metric === "sodium" ? 10 : 1}
              value={target}
              onChange={e => setTarget(e.target.value)}
              placeholder={metric === "calories" ? "e.g. 2000" : "e.g. 50"}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Goal name <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={`Daily ${metricInfo.label} Goal`}
            />
          </div>

          <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            This goal will also appear on the main <strong>Goals</strong> page where you can set milestones and add notes.
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
            <Button onClick={handleSave} disabled={isPending}>
              {isPending ? "Saving…" : isEdit ? "Save changes" : "Create goal"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Today's snapshot panel ────────────────────────────────────────────────

interface TodayData {
  calories: number; protein: number; carbs: number; fat: number;
  saturatedFat: number; transFat: number; sugar: number; fiber: number; sodium: number;
}

function TodayPanel({ today, trackedKeys }: { today: TodayData | null; trackedKeys: Set<string> }) {
  if (!today || trackedKeys.size === 0) return null;
  const tracked = FOOD_METRICS.filter(m => trackedKeys.has(m.key));

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">Today's Totals</span>
          <Badge variant="outline" className="text-[10px] ml-auto">
            {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {tracked.map(({ key, label, unit, color, Icon }) => {
            const val = (today as any)[key] ?? 0;
            return (
              <div key={key} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: color + "10" }}>
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
                <div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                  <div className="font-semibold text-sm">
                    {val % 1 === 0 ? val : val.toFixed(1)}
                    <span className="text-xs font-normal text-muted-foreground ml-0.5">{unit}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

type DiaryDay = TodayData & { date: string };

export default function FoodGoalPage() {
  const { toast } = useToast();
  const [dialogState, setDialogState] = useState<DialogState>({ open: false, goal: null });
  const [sharingGoal, setSharingGoal] = useState<Goal | null>(null);

  const { data: allGoals = [], isLoading: goalsLoading } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const { data: trends = [], isLoading: trendsLoading } = useQuery<DiaryDay[]>({
    queryKey: ["/api/health/diary/trends"],
    queryFn: () => fetch("/api/health/diary/trends?days=7").then(r => r.json()),
  });

  const foodGoals = useMemo(
    () => allGoals.filter(g => (g as any).goalSource === "food"),
    [allGoals]
  );

  const usedMetrics = useMemo(
    () => new Set(foodGoals.map(g => (g as any).healthMetric as string).filter(Boolean)),
    [foodGoals]
  );

  const today: TodayData | null = trends.length > 0 ? trends[trends.length - 1] : null;

  const getTrend = (metricKey: string): number[] =>
    trends.map(d => (d as any)[metricKey] ?? 0);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/goals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      toast({ title: "Food goal deleted" });
    },
    onError: () => {
      toast({ title: "Couldn't delete the food goal", description: "Please try again.", variant: "destructive" });
    },
  });

  const openCreate = () => setDialogState({ open: true, goal: null });
  const openEdit = (g: Goal) => setDialogState({ open: true, goal: g });
  const closeDialog = () => setDialogState({ open: false, goal: null });

  const isLoading = goalsLoading || trendsLoading;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="w-full px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-green-100 dark:bg-green-900/30">
              <Salad className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Food Goals</h1>
              <p className="text-sm text-muted-foreground">Daily nutrition targets synced with your meal diary</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/goals">
              <Button variant="outline" size="sm" className="hidden sm:flex items-center gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" />
                All Goals
              </Button>
            </Link>
            <Button onClick={openCreate} className="flex items-center gap-1.5">
              <Plus className="w-4 h-4" />
              Add Goal
            </Button>
          </div>
        </div>

        {/* Today's snapshot */}
        <TodayPanel today={today} trackedKeys={usedMetrics} />

        {/* Loading */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2].map(i => (
              <div key={i} className="h-52 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        )}

        {/* Goals grid */}
        {!isLoading && foodGoals.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {foodGoals.map(goal => (
              <GoalCard
                key={goal.id}
                goal={goal}
                todayValue={today ? ((today as any)[(goal as any).healthMetric] ?? 0) : 0}
                trendData={getTrend((goal as any).healthMetric ?? "")}
                onEdit={openEdit}
                onDelete={id => deleteMutation.mutate(id)}
                onShare={setSharingGoal}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && foodGoals.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="p-4 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
              <Salad className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No food goals yet</h2>
            <p className="text-muted-foreground max-w-xs mb-6">
              Set daily nutrition targets — calories, protein, sugar and more — and track them against your meal diary.
            </p>
            <Button onClick={openCreate} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create your first food goal
            </Button>
          </div>
        )}

        {/* Info footer */}
        {foodGoals.length > 0 && (
          <p className="text-center text-xs text-muted-foreground mt-8">
            Progress updates automatically from your <Link href="/meal-planner" className="underline underline-offset-2">Meal Planner</Link> diary.
            Food goals also appear on the <Link href="/goals" className="underline underline-offset-2">Goals</Link> page.
          </p>
        )}
      </div>

      {/* Dialog */}
      {dialogState.open && (
        <GoalDialog
          state={dialogState}
          onClose={closeDialog}
          usedMetrics={usedMetrics}
        />
      )}

      {sharingGoal && (
        <ShareDialog
          isOpen={!!sharingGoal}
          onClose={() => setSharingGoal(null)}
          itemId={sharingGoal.id}
          itemType="food_goal"
          itemTitle={sharingGoal.title}
        />
      )}
    </div>
  );
}
